import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { InputSwitch } from 'primereact/inputswitch';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SecurityIcon from '@mui/icons-material/Security';
import SearchIcon from '@mui/icons-material/Search';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Langs, { Localize } from '../../app/lang/langs';
import { getManagersProfiles, editManagerProfile, addManagerProfile } from '../../components/permissions/permissionsSlice';
import { getTeams } from '../../components/teams/teamsSlice';
import ProfileModal, { IsManagerLabel } from './ProfileModal';
import MobileProfileList from './mobile/MobileProfileList';
import pStyles from './Permissions.module.css';


export default function ProfileManagementTab({ onViewPermissions }) {
    let dispatch = useDispatch()
    let managersProfiles = useSelector((state) => state.permissions.managersProfiles)
    let allTeams = useSelector((state) => state.teams.allTeams)
    let isMobile = useSelector((state) => state.settings.isMobile)
    let isPhone = isMobile === 2

    let [globalFilter, setGlobalFilter] = useState('')
    let [modalVisible, setModalVisible] = useState(false)
    let [isAddMode, setIsAddMode] = useState(false)
    let [editingProfile, setEditingProfile] = useState(null)

    useEffect(() => {
        dispatch(getManagersProfiles({ all: true }))
        dispatch(getTeams())
    }, [dispatch])

    let teamOptions = allTeams.map(t => ({ label: t.label, value: t.value }))

    let openAddModal = () => {
        setIsAddMode(true)
        setEditingProfile(null)
        setModalVisible(true)
    }

    let openEditModal = (profile) => {
        setIsAddMode(false)
        setEditingProfile(profile)
        setModalVisible(true)
    }

    let handleSave = async ({ name, description, active, displayable, isManager, teamIds, inheritFromProfileId }) => {
        let action
        if (isAddMode) {
            action = await dispatch(addManagerProfile({
                name,
                description,
                displayable,
                is_manager: isManager,
                teamIds: teamIds.length ? teamIds : null,
                inheritFromProfileId: inheritFromProfileId || null
            }))
        } else {
            action = await dispatch(editManagerProfile({
                id: editingProfile.id,
                name,
                description,
                active,
                displayable,
                is_manager: isManager,
                teamIds
            }))
        }

        if (action.payload?.responseData?.status === 1) {
            setModalVisible(false)
            dispatch(getManagersProfiles({ all: true }))
        }
    }

    // Inline toggle: the only way to set the flag on locked profiles, which have no edit modal.
    // Refetch with `all: true` like every other refetch on this screen — a plain `{}` returns
    // the curated list, which would drop hidden profiles out of the admin table the moment
    // someone flipped a switch, leaving no way back to un-hide them.
    let toggleIsManager = async (profile, value) => {
        let action = await dispatch(editManagerProfile({ id: profile.id, is_manager: value }))
        if (action.payload?.responseData?.status === 1) {
            dispatch(getManagersProfiles({ all: true }))
        }
    }

    let isManagerBodyTemplate = (rowData) => (
        <InputSwitch
            checked={!!rowData.is_manager}
            onChange={(e) => toggleIsManager(rowData, e.value)}
            onClick={(e) => e.stopPropagation()}
        />
    )

    let activeBodyTemplate = (rowData) => {
        return <Tag
            value={rowData.active ? Localize('active') : Localize('inactive')}
            severity={rowData.active ? 'success' : 'danger'}
        />
    }

    // Rows fetched from a server without the column report no `displayable`; show those
    // as displayable rather than as hidden.
    let displayableBodyTemplate = (rowData) => {
        let shown = rowData.displayable === undefined ? true : !!rowData.displayable
        return <Tag
            value={shown ? Localize('PROFILE_DISPLAYABLE') : Localize('PROFILE_HIDDEN')}
            severity={shown ? 'success' : 'warning'}
        />
    }

    let teamsBodyTemplate = (rowData) => {
        if (!rowData.teams || !rowData.teams.length) {
            return <Tag value={Localize('ALL_TEAMS')} severity="info" />
        }
        let maxVisible = 2
        let visible = rowData.teams.slice(0, maxVisible)
        let remaining = rowData.teams.slice(maxVisible)
        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                {visible.map(t => (
                    <Tag key={t.id} value={t.name} severity="warning" style={{ fontSize: '0.75rem' }} />
                ))}
                {remaining.length > 0 && (
                    <Tooltip
                        title={
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: 4 }}>
                                {remaining.map(t => (
                                    <Chip
                                        key={t.id}
                                        label={t.name}
                                        size="small"
                                        sx={{
                                            bgcolor: 'rgba(255, 144, 21, 0.15)',
                                            color: '#fff',
                                            fontWeight: 500,
                                            fontSize: '0.75rem',
                                        }}
                                    />
                                ))}
                            </div>
                        }
                        arrow
                        placement="top"
                    >
                        <Chip
                            label={`+${remaining.length}`}
                            size="small"
                            sx={{
                                bgcolor: 'rgba(255, 144, 21, 0.12)',
                                color: '#FF9015',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                                '&:hover': { bgcolor: 'rgba(255, 144, 21, 0.2)' },
                            }}
                        />
                    </Tooltip>
                )}
            </div>
        )
    }

    const lockedProfileIds = [1, 2, 3, 4]

    let sortedProfiles = [...managersProfiles].sort((a, b) => {
        let aLocked = lockedProfileIds.includes(a.id)
        let bLocked = lockedProfileIds.includes(b.id)
        if (aLocked !== bLocked) return aLocked ? -1 : 1
        if (aLocked && bLocked) return a.id - b.id
        return 0
    })

    let actionsBodyTemplate = (rowData) => {
        let isLocked = lockedProfileIds.includes(rowData.id)
        return (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                {!isLocked && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon sx={{ fontSize: '16px !important' }} />}
                        onClick={() => openEditModal(rowData)}
                        className={pStyles.editBtn}
                    >
                        <Langs str="edit" />
                    </Button>
                )}
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SecurityIcon sx={{ fontSize: '16px !important' }} />}
                    onClick={() => onViewPermissions && onViewPermissions(rowData.name)}
                    className={pStyles.viewPermBtn}
                >
                    <Langs str="VIEW_PERMISSIONS" />
                </Button>
            </div>
        )
    }

    return (
        <div className={`profile-mgmt-card ${pStyles.profileCard}`}>
            <div className={pStyles.profileHeaderRow}>
                <TextField
                    value={globalFilter}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    placeholder={Localize('SEARCH')}
                    size="small"
                    // A fixed 260px field next to the Add button overflows a 360px
                    // viewport, which pushed the whole page sideways.
                    sx={{
                        width: { xs: '100%', sm: 260 },
                        mr: { sm: 'auto' },
                        '& .MuiInputBase-input': { fontSize: { xs: '16px', sm: '0.875rem' } },
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 20, color: 'rgba(0,40,128,0.4)' }} />
                            </InputAdornment>
                        ),
                    }}
                />
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openAddModal}
                    className={pStyles.addProfileBtn}
                    sx={{ width: { xs: '100%', sm: 'auto' }, minHeight: { xs: 48, sm: 'auto' } }}
                >
                    <Langs str="ADD_PROFILE" />
                </Button>
            </div>

            {isPhone ? (
                <MobileProfileList
                    profiles={sortedProfiles}
                    filter={globalFilter}
                    lockedProfileIds={lockedProfileIds}
                    onEdit={openEditModal}
                    onViewPermissions={onViewPermissions}
                    onToggleIsManager={toggleIsManager}
                />
            ) : (
            <DataTable
                value={sortedProfiles}
                globalFilter={globalFilter}
                paginator
                rows={10}
                rowsPerPageOptions={[5, 10, 25]}
                emptyMessage={Localize('NO_RESULTS')}
                rowClassName={() => 'cursor-pointer'}
                onRowDoubleClick={(e) => !lockedProfileIds.includes(e.data.id) && openEditModal(e.data)}
                scrollable
                scrollHeight="calc(100vh - 380px)"
                style={{ borderRadius: 14, overflow: 'hidden' }}
            >
                <Column field="name" header={Localize('name')} sortable style={{ minWidth: '10rem' }} />
                <Column field="description" header={Localize('description')} style={{ minWidth: '14rem' }} />
                <Column header={Localize('status')} body={activeBodyTemplate} sortable sortField="active" style={{ minWidth: '8rem' }} />
                <Column header={Localize('PROFILE_DISPLAYABLE')} body={displayableBodyTemplate} sortable sortField="displayable" style={{ minWidth: '9rem' }} />
                <Column header={<IsManagerLabel />} body={isManagerBodyTemplate} sortable sortField="is_manager" style={{ minWidth: '9rem' }} />
                <Column header={Localize('TEAMS')} body={teamsBodyTemplate} style={{ minWidth: '16rem' }} />
                <Column header="" body={actionsBodyTemplate} style={{ width: '18rem', textAlign: 'center' }} />
            </DataTable>
            )}

            <ProfileModal
                visible={modalVisible}
                onHide={() => setModalVisible(false)}
                isAddMode={isAddMode}
                editingProfile={editingProfile}
                onSave={handleSave}
                teamOptions={teamOptions}
                profileOptions={managersProfiles}
            />
        </div>
    )
}
